import { createPortal } from "react-dom";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet, apiPost } from "../../api/client";
import { OutputRender } from "../../components/review/OutputRender";
import { Modal } from "../../components/review/Modal";
import type { AnchoredQuote, HighlightedComment } from "../../components/review/ArticleRender";

interface Attachment {
  id: string;
  kind: "url" | "file";
  url: string | null;
  filename: string | null;
}

interface UpstreamStep {
  id: string;
  ord: number;
  type: string;
  title: string;
  state: string;
  output: unknown;
  successCriteria: string;
  attachments: Attachment[];
}

interface CommentRow {
  id: string;
  authorUserId: string;
  authorLabel: string | null;
  body: string;
  decision: "approve" | "return" | "note" | null;
  anchor: { quote: string; contextBefore?: string; contextAfter?: string } | null;
  createdAt: string;
  attachments: Attachment[];
}

interface AllowedAction {
  action: string;
  decision: "approve" | "return" | "note";
  label: string;
  requiresComment: boolean;
}

interface ReviewContext {
  step: {
    id: string;
    title: string;
    description: string;
    type: string;
    state: string;
    successCriteria: string;
    output: unknown;
    displayOwner: string | null;
  };
  upstream: UpstreamStep[];
  attachments: Attachment[];
  comments: CommentRow[];
  card:
    | {
        id: string;
        type: string;
        state: string;
        body?: Record<string, unknown>;
        allowedActions?: AllowedAction[];
        isRecipient?: boolean;
      }
    | null;
}

function Attachments({ items }: { items: Attachment[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="review-attach-list">
      {items.map((a) => (
        <li key={a.id}>
          📎{" "}
          {a.url ? (
            <a href={a.url} target="_blank" rel="noopener noreferrer">
              {a.filename || hostnameOf(a.url) || "link"}
            </a>
          ) : (
            a.filename || "(file)"
          )}
        </li>
      ))}
    </ul>
  );
}

function hostnameOf(url: string): string | null {
  try { return new URL(url).hostname; } catch { return null; }
}

export function TaskReviewPage() {
  const { workspaceId, stepId } = useParams<{ workspaceId: string; stepId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const [rejectToOpen, setRejectToOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentLabel, setAttachmentLabel] = useState("");
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submittedLabel, setSubmittedLabel] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<AnchoredQuote | null>(null);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);

  const ctxQ = useQuery<ReviewContext, ApiError>({
    queryKey: ["task-review", workspaceId, stepId],
    queryFn: () =>
      apiGet<ReviewContext>(
        `/api/workspaces/${workspaceId}/steps/${stepId}/review-context`,
      ),
    enabled: !!workspaceId && !!stepId,
    retry: false,
  });

  const submitM = useMutation<
    unknown,
    ApiError,
    {
      action: string;
      comment?: string;
      anchor?: AnchoredQuote;
      attachments?: Array<{ kind: "url"; url: string; filename?: string }>;
    }
  >({
    mutationFn: (body) =>
      apiPost(
        `/api/workspaces/${workspaceId}/steps/${stepId}/review-action`,
        body,
      ),
    onSuccess: async (_data, vars) => {
      const preset = view?.card?.allowedActions?.find((a) => a.action === vars.action);
      setSubmittedLabel(preset?.label ?? vars.action);
      setSuccessOpen(true);
      await qc.invalidateQueries({ queryKey: ["task-review", workspaceId, stepId] });
      await qc.invalidateQueries({ queryKey: ["daily-brief"] });
    },
    onSettled: () => setPendingAction(null),
  });

  const rejectToM = useMutation<unknown, ApiError, { targetStepId: string; comment?: string }>({
    mutationFn: (body) =>
      apiPost(
        `/api/workspaces/${workspaceId}/steps/${stepId}/reject-to`,
        body,
      ),
    onSuccess: async () => {
      setRejectToOpen(false);
      setSubmittedLabel("Rejected — step reset");
      setSuccessOpen(true);
      await qc.invalidateQueries({ queryKey: ["task-review", workspaceId, stepId] });
      await qc.invalidateQueries({ queryKey: ["daily-brief"] });
    },
  });

  const view = ctxQ.data;

  const pendingAttachments = useMemo(() => {
    const u = attachmentUrl.trim();
    if (!u) return [];
    return [{ kind: "url" as const, url: u, ...(attachmentLabel.trim() && { filename: attachmentLabel.trim() }) }];
  }, [attachmentUrl, attachmentLabel]);

  // Hooks must run on every render — derive highlights before any early
  // return below, even when `view` is undefined.
  const highlights: HighlightedComment[] = useMemo(() => {
    if (!view) return [];
    return view.comments
      .filter((c) => c.anchor?.quote)
      .map((c) => ({
        id: c.id,
        quote: c.anchor!.quote,
        authorLabel: c.authorLabel ?? c.authorUserId,
        decision: c.decision,
      }));
  }, [view]);

  if (ctxQ.isLoading) {
    return <div className="review-page">Loading task…</div>;
  }

  if (ctxQ.error || !view) {
    return (
      <div className="review-page">
        <button type="button" className="review-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="review-banner error">Could not load this task.</div>
      </div>
    );
  }

  const allowedActions = view.card?.allowedActions ?? [];
  const isOpen = view.card?.state === "open";
  const isRecipient = view.card?.isRecipient === true;
  const canAct = isOpen && isRecipient && allowedActions.length > 0;
  const cardSummary = view.card?.body
    ? ((view.card.body["output_summary"] as string | undefined) ??
       (view.card.body["context"] as string | undefined) ??
       (view.card.body["proposed_brief"] as string | undefined))
    : undefined;

  function onAction(actionKey: string) {
    const preset = allowedActions.find((a) => a.action === actionKey);
    if (!preset) return;
    if (preset.requiresComment && comment.trim().length === 0) {
      setCommentError(
        `A comment is required to ${preset.label.toLowerCase()}.`,
      );
      return;
    }
    setCommentError(null);
    // "reject" action opens the reject-to step picker before submitting.
    if (actionKey === "reject") {
      const defaultTarget = view?.upstream?.[view.upstream.length - 1]?.id ?? "";
      setRejectTargetId(defaultTarget);
      setRejectToOpen(true);
      return;
    }
    setPendingAction(actionKey);
    submitM.mutate({
      action: actionKey,
      ...(comment.trim() && { comment: comment.trim() }),
      ...(anchor && { anchor }),
      ...(pendingAttachments.length > 0 && { attachments: pendingAttachments }),
    });
  }

  function onQuoteSelected(q: AnchoredQuote) {
    setAnchor(q);
    setCommentError(null);
    requestAnimationFrame(() => {
      commentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      commentRef.current?.focus();
    });
  }

  return (
    <>
    <div className="review-page">
      <button
        type="button"
        className="review-back"
        onClick={() => navigate(`/workspaces/${workspaceId}`)}
      >
        ← My tasks
      </button>

      <p className="review-eyebrow">{prettyType(view.card?.type ?? view.step.type)}</p>
      <h1 className="review-title">{view.step.title}</h1>

      {(view.step.successCriteria || cardSummary) && (
        <div className="review-meta">
          {cardSummary && (
            <div style={{ marginBottom: view.step.successCriteria ? 6 : 0 }}>
              {cardSummary}
            </div>
          )}
          {view.step.successCriteria && (
            <div>
              <strong>Success criteria:</strong> {view.step.successCriteria}
            </div>
          )}
        </div>
      )}

      <Modal
        open={successOpen}
        tone="ok"
        title={`Submitted: ${submittedLabel ?? ""}`}
        body="Your decision was recorded. The routine continues."
        primaryLabel="Back to My tasks"
        secondaryLabel="Stay here"
        onPrimary={() => {
          setSuccessOpen(false);
          navigate(`/workspaces/${workspaceId}`);
        }}
        onSecondary={() => setSuccessOpen(false)}
      />

      <Modal
        open={
          !!submitM.error &&
          (submitM.error.status === 409 || submitM.error.status === 410)
        }
        tone={submitM.error?.status === 410 ? "warn" : "warn"}
        title={
          submitM.error?.status === 410
            ? "This review link expired"
            : "Already actioned"
        }
        body={
          submitM.error?.status === 410
            ? "Please request a fresh notification from WorkForceOS."
            : "Someone else has already responded to this task."
        }
        primaryLabel="Close"
        onPrimary={() => submitM.reset()}
      />

      {view.upstream.length > 0 && (
        <section className="review-section">
          <h2 className="review-section-title">What got us here</h2>
          <div className="review-upstream">
            {view.upstream.map((u) => (
              <details key={u.id} open={view.upstream.length <= 2}>
                <summary>
                  <strong>{u.title}</strong>
                  <span className="review-upstream-meta">
                    {prettyType(u.type)} · {prettyState(u.state)}
                  </span>
                </summary>
                <div className="review-upstream-body">
                  <OutputRender output={u.output} />
                  <Attachments items={u.attachments} />
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {(view.step.output != null || view.attachments.length > 0) && (
        <section className="review-section">
          <h2 className="review-section-title">This step's output</h2>
          <OutputRender
            output={view.step.output}
            onQuoteSelected={canAct ? onQuoteSelected : undefined}
            highlights={highlights}
          />
          {view.attachments.length > 0 && <Attachments items={view.attachments} />}
        </section>
      )}

      {view.comments.length > 0 && (
        <section className="review-section">
          <h2 className="review-section-title">Prior comments</h2>
          <ul className="review-comment-list">
            {view.comments.map((c) => (
              <li key={c.id} className="review-comment">
                <div className="review-comment-head">
                  <span className="author">{c.authorLabel ?? c.authorUserId}</span>
                  {c.decision && (
                    <span className={`review-comment-decision ${c.decision}`}>
                      {c.decision === "approve" ? "approved" :
                       c.decision === "return"  ? "requested changes" :
                                                  "note"}
                    </span>
                  )}
                  <span>· {formatTimestamp(c.createdAt)}</span>
                </div>
                {c.anchor?.quote && (
                  <div className="review-anchor-chip">
                    {c.anchor.quote.length > 220
                      ? `${c.anchor.quote.slice(0, 220)}…`
                      : c.anchor.quote}
                  </div>
                )}
                <p className="review-comment-body">{c.body}</p>
                <Attachments items={c.attachments} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {canAct ? (
        <section className="review-form">
          <h2 className="review-section-title" style={{ marginBottom: 14 }}>Your response</h2>

          <label className="review-field">
            <span className="review-field-label">
              <strong>Comment</strong>
              <span className="review-field-hint">
                {anchor ? "anchored to your selection" : "required when requesting changes or rejecting"}
              </span>
              {anchor && (
                <button
                  type="button"
                  onClick={() => setAnchor(null)}
                  style={{
                    marginLeft: "auto",
                    background: "none",
                    border: 0,
                    color: "var(--text-3, #64748b)",
                    cursor: "pointer",
                    fontSize: 11,
                    textDecoration: "underline",
                  }}
                >
                  remove anchor
                </button>
              )}
            </span>
            {anchor && (
              <div className="review-anchor-chip">
                {anchor.quote.length > 220 ? `${anchor.quote.slice(0, 220)}…` : anchor.quote}
              </div>
            )}
            <textarea
              ref={commentRef}
              className="review-textarea"
              rows={4}
              placeholder={anchor ? "What about this passage?" : "Add context, concerns, or sign-off rationale…"}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (commentError && e.target.value.trim().length > 0) {
                  setCommentError(null);
                }
              }}
              aria-invalid={!!commentError}
              style={commentError ? { borderColor: "#dc2626" } : undefined}
            />
            {commentError && (
              <div style={{ marginTop: 6, color: "#b91c1c" }}>
                {commentError}
              </div>
            )}
          </label>

          <div className="review-attach-row" style={{ marginTop: 12 }}>
            <label className="review-field" style={{ marginTop: 0 }}>
              <span className="review-field-label">
                <strong>Attachment URL</strong>
                <span className="review-field-hint">optional — Drive, Dropbox, etc.</span>
              </span>
              <input
                className="review-input"
                type="url"
                placeholder="https://drive.google.com/…"
                value={attachmentUrl}
                onChange={(e) => setAttachmentUrl(e.target.value)}
              />
            </label>
            <label className="review-field" style={{ marginTop: 0 }}>
              <span className="review-field-label">
                <strong>Label</strong>
              </span>
              <input
                className="review-input"
                type="text"
                placeholder="optional"
                value={attachmentLabel}
                onChange={(e) => setAttachmentLabel(e.target.value)}
              />
            </label>
          </div>

          {submitM.error && submitM.error.status !== 409 && submitM.error.status !== 410 && (
            <div className="review-banner error">
              {submitM.error.status === 403
                ? "You're not the recipient of this task."
                : `Could not submit (${submitM.error.status}).`}
            </div>
          )}

          <div className="review-actions">
            {allowedActions.map((a) => {
              const pending = pendingAction === a.action;
              return (
                <button
                  key={a.action}
                  type="button"
                  disabled={submitM.isPending}
                  onClick={() => onAction(a.action)}
                  className={`review-btn ${a.decision}`}
                >
                  {pending ? "Submitting…" : a.label}
                </button>
              );
            })}
          </div>
        </section>
      ) : (
        <div className="review-readonly">
          {view.card?.state === "actioned" || view.card?.state === "auto_actioned"
            ? "This task has been actioned. View only."
            : view.card?.state === "expired"
            ? "This task expired before it was actioned."
            : !view.card
            ? "No open action is required for this step."
            : !isRecipient
            ? "You're not the recipient of this task — view only."
            : null}
        </div>
      )}
    </div>
    {rejectToOpen && createPortal(
      <RejectToModal
        upstream={view.upstream}
        currentStepOrd={view.step ? 999 : 999}
        targetId={rejectTargetId}
        onTargetChange={setRejectTargetId}
        comment={comment}
        pending={rejectToM.isPending}
        error={rejectToM.error?.message ?? null}
        onConfirm={() => {
          rejectToM.mutate({
            targetStepId: rejectTargetId,
            ...(comment.trim() && { comment: comment.trim() }),
          });
        }}
        onCancel={() => setRejectToOpen(false)}
      />,
      document.body,
    )}
    </>
  );
}

interface RejectToModalProps {
  upstream: UpstreamStep[];
  currentStepOrd: number;
  targetId: string;
  onTargetChange: (id: string) => void;
  comment: string;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function RejectToModal({
  upstream,
  targetId,
  onTargetChange,
  comment,
  pending,
  error,
  onConfirm,
  onCancel,
}: RejectToModalProps) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.45)", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "var(--surface, #fff)", borderRadius: 12,
          width: 480, maxHeight: "70vh", display: "flex",
          flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          padding: "22px 24px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Send back for rework</div>
        <div style={{ color: "var(--text-2)", marginBottom: 16 }}>
          Choose which prior step to reset. That step and all steps after it will revert to pending.
        </div>

        {upstream.length === 0 ? (
          <div style={{ color: "var(--text-3)", marginBottom: 16 }}>
            No prior steps to send back to. This is the first step.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16, overflowY: "auto", maxHeight: 240 }}>
            {[...upstream].reverse().map((s) => (
              <label
                key={s.id}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  border: `1.5px solid ${targetId === s.id ? "var(--accent, #6366f1)" : "var(--border)"}`,
                  borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                  background: targetId === s.id ? "color-mix(in srgb, var(--accent, #6366f1) 6%, transparent)" : "var(--surface-2, #f9f9f9)",
                }}
              >
                <input
                  type="radio"
                  name="reject-to"
                  value={s.id}
                  checked={targetId === s.id}
                  onChange={() => onTargetChange(s.id)}
                  style={{ marginTop: 2, accentColor: "var(--accent, #6366f1)" }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Step {s.ord + 1}: {s.title}</div>
                  <div style={{ color: "var(--text-3)", marginTop: 2, textTransform: "capitalize" }}>{s.state.replace(/_/g, " ")}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        {comment.trim() && (
          <div style={{ color: "var(--text-3)", marginBottom: 12, fontStyle: "italic" }}>
            Your comment will be attached: "{comment.trim().slice(0, 80)}{comment.trim().length > 80 ? "…" : ""}"
          </div>
        )}

        {error && (
          <div style={{ color: "var(--red, #dc2626)", marginBottom: 10 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn primary"
            disabled={!targetId || pending || upstream.length === 0}
            onClick={onConfirm}
            style={{ background: "var(--red, #dc2626)", borderColor: "var(--red, #dc2626)" }}
          >
            {pending ? "Sending back…" : "Confirm reject"}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function prettyType(t: string): string {
  return ({
    decision: "Decision",
    review: "Review",
    confirmation: "Confirmation",
    intervention: "Intervention",
    milestone: "Milestone",
    query: "Query",
    comm: "Communication",
    micro_task: "Task",
    wait: "Wait",
    instrumentation: "Instrumentation",
  } as Record<string, string>)[t] ?? t;
}

function prettyState(s: string): string {
  return s.replace(/_/g, " ");
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
